import React from 'react'
import {BiSolidChurch} from 'react-icons/bi'
import {PiCrossFill, PiHandHeart} from 'react-icons/pi'
import {BsPersonFill} from 'react-icons/bs'
import {FiUsers} from 'react-icons/fi'
import {FaUserTie} from 'react-icons/fa'
import {
    church_getParishes_Api,
    diocese_getById_Api,
    diocese_getDioceses_Api,
    organization_fetchItems_Api,
    parish_getById_Api,
    profile_getById_Api,
    profile_getProfiles_Api,
    religiousOrder_getById_Api,
    religiousOrder_getReligiousOrders_Api,
    yellowPage_fetchYellowPages,
    yellowPage_getById_Api,
} from 'apis'
import {getFullAddress} from 'views/Shared'
import {ParishFilter} from '../../Parish/filters/ParishFilter'
import {DirectoryDioceseFilter} from '../filters/DirectoryDioceseFilter'
import {DirectoryReligiousOrderFilter} from '../filters/DirectoryReligiousOrderFilter'
import {DirectoryYellowPageFilter} from '../filters/DirectoryYellowPageFilter'
import {DirectoryProfileFilter} from '../filters/DirectoryProfileFilter'
import {DirectoryOrganizationFilter} from '../filters/DirectoryOrganizationFilter'
import {useParishes} from '../../Parish/functions/useParishes'
import {CreateParishForm, EditParishForm} from '../../Parish/components/ParishForm'
import {useProfileFunc} from '../../Profile/functions/useProfileFunc'
import {CreateProfileForm, EditProfileForm} from '../../Profile/components/ProfileForm'
import {useDioceses} from '../../Diocese/functions/useDioceses'
import {CreateDioceseForm, EditDioceseForm} from '../../Diocese/components/DioceseForm'
import {useReligiousOrders} from '../../ReligiousOrder/functions/useReligiousOrders'
import {
    CreateReligiousOrderForm,
    EditReligiousOrderForm,
    TargetGenderTypes
} from '../../ReligiousOrder/components/ReligiousOrderForm'
import {NatureTypeInfos} from '../../ReligiousOrder/consts/NatureTypes'
import {useYellowPage} from '../../YellowPage/functions/useYellowPage'
import {CreateYellowPageForm, EditYellowPageForm} from '../../YellowPage/components/YellowPageForm'
import {staticPaths} from '../../../../routes/staticPaths'

// Rút gọn "Tổng Giáo phận X" / "Giáo phận X" -> "X", dùng cho badge vùng miền
export const shortenDioceseName = (name) => {
    if (!name) return null
    return name.replace(/^(Tổng\s+)?Giáo\s+phận\s+/i, '').trim() || name
}

export const DirectoryTypeKeys = Object.freeze({
    ALL: 'all',
    PARISH: 'parish',
    DIOCESE: 'diocese',
    RELIGIOUS_ORDER: 'religious_order',
    ORGANIZATION: 'organization',
    YELLOW_PAGE: 'yellow_page',
    PROFILE: 'profile',
})

export const directoryTypes = [
    {
        key: DirectoryTypeKeys.PARISH,
        owner_type: 'parish',
        label: 'parish',
        icon: (props) => <BiSolidChurch {...props} />,
        colorClass: 'text-blue-500',
        badgeBg: 'bg-blue-50 text-blue-600',
        apiInfo: church_getParishes_Api,
        FilterComponent: ParishFilter,
        fixedValues: {},
        statsKey: 'total_parishes',
        getBadge: (item) => shortenDioceseName(item?.diocese?.fullname || item?.diocese?.name),
        getAddress: (item) => item?.location_address || getFullAddress(item),
        // ParishTransformerHierarchy không có avatar riêng - ảnh thật nằm ở Organization liên kết
        // (item.owner, org_type=300); item.avatar chỉ có khi item chính là Organization đó luôn
        // (case "Tổ chức của tôi" - org_type map thẳng ra PARISH, không qua owner).
        getAvatar: (item) => item?.owner?.avatar || item?.avatar,
        crud: {
            useCrudFunc: useParishes,
            CreateForm: CreateParishForm,
            EditForm: EditParishForm,
            getByIdApiInfo: parish_getById_Api,
            addLabel: 'add parish',
            createTitle: 'create new parish',
            editTitle: 'edit parish information',
            editWidth: 800,
        },
    },
    {
        key: DirectoryTypeKeys.DIOCESE,
        owner_type: 'diocese',
        label: 'diocese',
        icon: (props) => <PiCrossFill {...props} />,
        colorClass: 'text-orange-500',
        badgeBg: 'bg-orange-50 text-orange-600',
        apiInfo: diocese_getDioceses_Api,
        FilterComponent: DirectoryDioceseFilter,
        fixedValues: {},
        statsKey: 'total_dioceses',
        getBadge: (item) => shortenDioceseName(item?.name),
        getAddress: (item) => item?.contact_address,
        // DioceseTransformerBasic dùng field 'photo' (không phải avatar) cho ảnh giáo phận.
        getAvatar: (item) => item?.photo || item?.avatar,
        crud: {
            useCrudFunc: useDioceses,
            CreateForm: CreateDioceseForm,
            EditForm: EditDioceseForm,
            getByIdApiInfo: diocese_getById_Api,
            addLabel: 'add diocese',
            createTitle: 'create new diocese',
            editTitle: 'edit diocese info',
            deleteTitle: 'delete this diocese',
            deleteMessage: 'are you sure you want to delete this diocese',
            editWidth: 740,
        },
    },
    {
        key: DirectoryTypeKeys.RELIGIOUS_ORDER,
        owner_type: 'religious_order',
        label: 'religious order',
        icon: (props) => <BsPersonFill {...props} />,
        colorClass: 'text-purple-500',
        badgeBg: 'bg-purple-50 text-purple-600',
        apiInfo: religiousOrder_getReligiousOrders_Api,
        FilterComponent: DirectoryReligiousOrderFilter,
        fixedValues: {},
        // Mặc định tab chỉ hiện đúng Dòng tu (giữ đúng tên/nghĩa tab) — khác fixedValues ở chỗ
        // user có thể ghi đè qua filter Bản chất để mở rộng xem Tu Hội Đời/Tu Đoàn Tông Đồ/CGTH.
        defaultFilterValues: {nature_typecd: 'religious_order'},
        statsKey: 'total_religious_orders',
        // Card gộp chung 1 typeConfig cho cả 4 bản chất (fixedValues rỗng, chỉ khoá mặc định qua
        // defaultFilterValues ở trên - user vẫn lọc thấy cả Tu Hội Đời/Tu Đoàn Tông Đồ/CGTH) - trước
        // đây badge luôn hiện cứng "Dòng tu" bất kể bản chất thật của item (bạn báo thiếu phân biệt).
        // getLabel trả đúng nature_typecd của TỪNG item, fallback về label tĩnh ở trên khi thiếu.
        getLabel: (item) => Object.values(NatureTypeInfos).find((n) => n.value === item?.nature_typecd)?.label,
        // Giới tính dòng tu (Nam/Nữ/Nam-Nữ) ghép cùng dòng với giáo phận - t truyền từ DirectoryCardRow
        // (getBadge là text thô không tự dịch như label, cần t() để dịch đúng nhãn giới tính).
        getBadge: (item, t) => {
            const genderLabel = Object.values(TargetGenderTypes).find((g) => g.value === Number(item?.target_gender))?.label
            return [
                shortenDioceseName(item?.diocese_name || item?.diocese?.name),
                genderLabel && t ? t(genderLabel) : null,
            ].filter(Boolean).join(' · ')
        },
        getAddress: (item) => item?.contact_address,
        // ReligiousOrderTransformerBasic dùng field 'photo' (không phải avatar) cho ảnh dòng tu.
        getAvatar: (item) => item?.photo || item?.avatar,
        crud: {
            useCrudFunc: useReligiousOrders,
            CreateForm: CreateReligiousOrderForm,
            EditForm: EditReligiousOrderForm,
            getByIdApiInfo: religiousOrder_getById_Api,
            addLabel: 'add religious order',
            createTitle: 'create new religious order',
            editTitle: 'edit religious order info',
            deleteTitle: 'delete this religious orders',
            deleteMessage: 'are you sure you want to delete this religious orders',
            editWidth: 800,
        },
    },
    {
        key: DirectoryTypeKeys.ORGANIZATION,
        owner_type: 'organization',
        label: 'organization',
        icon: (props) => <FiUsers {...props} />,
        colorClass: 'text-green-500',
        badgeBg: 'bg-green-50 text-green-600',
        apiInfo: organization_fetchItems_Api,
        FilterComponent: DirectoryOrganizationFilter,
        // fixedValues: {org_type: 999},
        statsKey: 'total_organizations',
        getBadge: (item) => item?.diocese?.fullname ? shortenDioceseName(item.diocese.fullname) : null,
        getAddress: () => null,
        // OrganizationTransformerBasic/Tiny dùng thẳng field 'avatar'.
        getAvatar: (item) => item?.avatar,
        // Không có CRUD inline (item của user, ko phải data hệ thống) - chỉ hướng dẫn tìm trước,
        // chưa có thì tạo mới qua trang tạo Cộng đoàn có sẵn.
        guide: {
            title: 'search organization guide title',
            description: 'search organization guide description',
            createLabel: 'create organization',
            createTo: staticPaths.createOrganization,
        },
    },
    {
        key: DirectoryTypeKeys.YELLOW_PAGE,
        owner_type: 'yellow_page',
        label: 'yellow pages',
        icon: (props) => <PiHandHeart {...props} />,
        colorClass: 'text-pink-500',
        badgeBg: 'bg-pink-50 text-pink-600',
        apiInfo: yellowPage_fetchYellowPages,
        FilterComponent: DirectoryYellowPageFilter,
        fixedValues: {},
        statsKey: 'total_yellow_pages',
        getTitle: (item) => item?.organization_name,
        getBadge: (item) => item?.province_name,
        getAddress: (item) => item?.address,
        // YellowPageTransformerBasic không có avatar riêng - ưu tiên logo tổ chức liên kết
        // (owner), không có thì lấy ảnh người liên hệ (contact_user).
        getAvatar: (item) => item?.owner?.avatar || item?.contact_user?.avatar,
        // Cùng quy ước với trang Bác Ái độc lập (containers/YellowPages.js) - thêm mới gate system
        // admin, sửa/xóa theo item?.edit (edit = isSystemAdmin() từ YellowPageTransformerBasic).
        crud: {
            useCrudFunc: useYellowPage,
            CreateForm: CreateYellowPageForm,
            EditForm: EditYellowPageForm,
            getByIdApiInfo: yellowPage_getById_Api,
            addLabel: 'add name card',
            createTitle: 'create new name card',
            editTitle: 'edit name card info',
            deleteTitle: 'delete this name card',
            deleteMessage: 'are you sure you want to delete this name card',
            editWidth: 740,
        },
    },
    {
        key: DirectoryTypeKeys.PROFILE,
        owner_type: 'profile',
        label: 'profile',
        icon: (props) => <FaUserTie {...props} />,
        colorClass: 'text-amber-600',
        badgeBg: 'bg-amber-50 text-amber-700',
        apiInfo: profile_getProfiles_Api,
        FilterComponent: DirectoryProfileFilter,
        fixedValues: {},
        statsKey: 'total_profiles',
        // Ghép chuỗi từ place_of_birth/place_of_death/location/năm sinh-mất — không có thì không hiển thị, không fallback
        getBadge: (item) => {
            const {place_of_birth, place_of_death, location, year_of_birth, year_of_death} = item ?? {}
            const places = [...new Set([place_of_birth, place_of_death].filter(Boolean))]
            if (places.length > 0) return places.join(' - ')
            if (location) return location
            if (year_of_birth || year_of_death) {
                return [year_of_birth, year_of_death].filter(Boolean).join(' - ')
            }
            return null
        },
        getAddress: (item) => null,
        // ProfileTransformerBasic có cả 'photo' (chân dung/ảnh tư liệu) và 'avatar' - trang chi
        // tiết Profile ưu tiên photo trước (xem ProfileContent.js), giữ đồng nhất ở đây.
        getAvatar: (item) => item?.photo || item?.avatar,
        crud: {
            useCrudFunc: useProfileFunc,
            CreateForm: CreateProfileForm,
            EditForm: EditProfileForm,
            getByIdApiInfo: profile_getById_Api,
            addLabel: 'add character',
            createTitle: 'create new character',
            editTitle: 'edit character info',
            deleteTitle: 'delete this profile',
            deleteMessage: 'are you sure you want to delete this profile',
        },
    },
]

export const getDirectoryTypeByKey = (key) => directoryTypes.find((e) => e.key === key)
